import { useState, useMemo, useEffect } from "react"
import {
  Search,
  ExternalLink,
  Plus,
  Pencil,
  Copy,
  CopyX,
  Archive,
  Trash2,
  Check,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  ChevronLeft,
  ChevronRight,
  Eye,
  EyeOff,
} from "lucide-react"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table"
import { Badge } from "../components/ui/badge"
import {
  Dialog,
  DialogTrigger,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogClose,
} from "../components/ui/dialog"
import { useLogs } from "../components/LogProvider"
import { useLocale } from "../components/LocaleProvider"
import { useAuth } from "../components/AuthProvider"
import { toast } from "../components/ui/toast"
import { type AccountOption, type CreateDashboardDto } from "../lib/api"
import {
  useDashboardRecords,
  useAccountOptions,
  useClients,
  useCreateDashboardRecord,
  useUpdateDashboardRecord,
  useArchiveDashboardRecord,
  useDeleteDashboardRecord,
  useDeleteDashboardRecords,
} from "../lib/queries"
import type { DashboardRecord, Client } from "../types"
import type { TranslationKey } from "../i18n/translations"
import { getUserColor } from "../lib/utils"
import { Switch } from "../components/ui/switch"

const STATUSES = [
  "Request submitted",
  "Termin invitation",
  "Waiting",
  "Completed",
  "No relevant",
  "Denied",
  "Absage",
  "Error",
] as const

const PAGE_SIZE = 100

const HIDE_SENSITIVE_STORAGE_KEY = "dashboard_hide_sensitive"

const STATUS_T_KEY: Record<string, TranslationKey> = {
  "Request submitted": "status_request_submitted",
  "Termin invitation": "status_termin_invitation",
  Waiting: "status_waiting",
  Completed: "status_completed",
  "No relevant": "status_no_relevant",
  Denied: "status_denied",
  Absage: "status_absage",
  Error: "status_error",
}

const STRATEGY_SHORT: Record<string, string> = {
  Active: "A",
  Outsource: "O",
  "Passive 1": "P1",
  "Passive 2": "P2",
  "Passive 3": "P3",
  "Passive 4": "P4",
  "Passive 5": "P5",
}

const STRATEGY_T_KEY: Record<string, TranslationKey> = {
  Active: "strategy_active",
  Outsource: "strategy_outsource",
  "Passive 1": "strategy_passive_1",
  "Passive 2": "strategy_passive_2",
  "Passive 3": "strategy_passive_3",
  "Passive 4": "strategy_passive_4",
  "Passive 5": "strategy_passive_5",
}

const STRATEGIES = [
  "Active",
  "Outsource",
  "Passive 1",
  "Passive 2",
  "Passive 3",
  "Passive 4",
  "Passive 5",
]

const MAIL_LABEL: Record<string, string> = {
  M2: "G2",
  M3: "M3",
  M4: "F4",
  M5: "R5",
  M6: "T6",
}

const ORIGINS = ["Immoscout24", "Immovelt", "Telegram"]

const SOURCES = ["Manual", "Immomio", "Auto"]

const SOURCE_ORDER: Record<string, number> = {
  Manual: 0,
  Immomio: 1,
  Auto: 2,
}

const emptyMails = { M2: false, M3: false, M4: false, M5: false, M6: false }

const emptyForm: DashboardRecord = {
  date: "",
  time: "",
  client: "",
  account: "",
  strategy: "",
  objectId: "",
  objectLink: "",
  status: "Request submitted",
  terminDate: "",
  comment: "",
  cooperative: "",
  origin: "",
  source: "Manual",
  mails: { ...emptyMails },
}

function extractExposeId(url: string): string | null {
  if (!url) return null
  const expose = url.match(/\/expose\/(\d+)/)
  if (expose) return expose[1]
  // Fall back to the last numeric path segment (e.g. .../appointments/3745525064)
  const segments = Array.from(url.matchAll(/\/\d{3,}(?=[/?#]|$)/g))
  const last = segments.pop()
  return last ? last[0].slice(1) : null
}

function statusVariant(status: string): "success" | "info" | "warning" | "danger" | "neutral" {
  if (status === "Completed") return "success"
  if (status === "Request submitted") return "info"
  if (status === "Termin invitation") return "danger"
  if (status === "Waiting") return "warning"
  if (status === "Denied") return "neutral"
  if (status === "Absage") return "warning"
  if (status === "Error") return "danger"
  return "neutral"
}

function calcAlive(date: string, time: string): string {
  if (!date || !time) return "—"
  const created = new Date(`${date}T${time}:00`)
  if (isNaN(created.getTime())) return "—"
  const diffMs = Date.now() - created.getTime()
  if (diffMs < 0) return "—"
  const totalSeconds = Math.floor(diffMs / 1000)
  const days = Math.floor(totalSeconds / 86400)
  const hours = Math.floor((totalSeconds % 86400) / 3600)
  const minutes = Math.floor((totalSeconds % 3600) / 60)
  const seconds = totalSeconds % 60
  if (days === 0 && hours === 0) return `${minutes}m ${seconds}s`
  if (days === 0) return `${hours}H ${minutes}m`
  return `${days}D ${hours}H`
}

// How far a record has progressed through the workflow (used to decide
// which duplicate to keep when bulk-deleting)
const STATUS_PROGRESS: Record<string, number> = {
  Error: 0,
  "Request submitted": 1,
  Waiting: 2,
  "Termin invitation": 3,
  "No relevant": 4,
  Denied: 4,
  Absage: 4,
  Completed: 5,
}

function createdTimestamp(record: DashboardRecord): number {
  const ts = new Date(`${record.date}T${record.time}:00`).getTime()
  return isNaN(ts) ? Number.MAX_SAFE_INTEGER : ts
}

function keepScore(record: DashboardRecord): number {
  const mails = Object.values(record.mails).filter(Boolean).length
  const progress = STATUS_PROGRESS[record.status] ?? 0
  const detail = [record.comment, record.terminDate, record.cooperative, record.origin].filter(
    Boolean,
  ).length
  return mails * 1000 + progress * 100 + detail * 10
}

function Dashboard() {
  const { user } = useAuth()
  const isAdmin = user?.role === "ADMIN"
  const { data: dashboardData, isLoading } = useDashboardRecords()
  const dashboardRecords: DashboardRecord[] = dashboardData ?? []
  const { data: accountsData } = useAccountOptions()
  const accounts: AccountOption[] = accountsData ?? []
  const createMut = useCreateDashboardRecord()
  const updateMut = useUpdateDashboardRecord()
  const archiveMut = useArchiveDashboardRecord()
  const deleteMut = useDeleteDashboardRecord()
  const deleteDupMut = useDeleteDashboardRecords()

  const { logAction } = useLogs()
  const { t } = useLocale()
  const { data: clientsData } = useClients()
  const clients: Client[] = clientsData ?? []
  const [search, setSearch] = useState("")
  const [operatorFilter, setOperatorFilter] = useState("")
  const [copiedIndex, setCopiedIndex] = useState<number | null>(null)

  const [open, setOpen] = useState(false)
  const [form, setForm] = useState(emptyForm)
  const [editingId, setEditingId] = useState<number | null>(null)
  const [duplicateError, setDuplicateError] = useState("")
  const [urlError, setUrlError] = useState("")
  const [accountFilter, setAccountFilter] = useState("")
  const [duplicatesOnly, setDuplicatesOnly] = useState(false)
  const [dupOpen, setDupOpen] = useState(false)
  const [dupConfirmText, setDupConfirmText] = useState("")
  const [removingDup, setRemovingDup] = useState(false)
  const [deletingId, setDeletingId] = useState<number | null>(null)
  const [statusSort, setStatusSort] = useState<"none" | "asc" | "desc">("none")
  const [sourceSort, setSourceSort] = useState<"none" | "asc" | "desc">("none")
  const [terminSort, setTerminSort] = useState<"none" | "asc" | "desc">("none")
  const [page, setPage] = useState(1)
  const [hideSensitive, setHideSensitive] = useState(
    () => sessionStorage.getItem(HIDE_SENSITIVE_STORAGE_KEY) === "true",
  )

  useEffect(() => {
    sessionStorage.setItem(HIDE_SENSITIVE_STORAGE_KEY, String(hideSensitive))
  }, [hideSensitive])

  const clientColorMap = useMemo(
    () => Object.fromEntries(clients.map((c) => [c.fullName, c.color || getUserColor(c.fullName)])),
    [clients],
  )

  const accountClientMap = useMemo(
    () => Object.fromEntries(accounts.map((a) => [a.accountId, a.clientName])),
    [accounts],
  )

  const accountList = useMemo(() => accounts.map((a) => a.accountId).sort(), [accounts])

  const uniqueAccounts = useMemo(
    () => Array.from(new Set(dashboardRecords.map((r) => r.account))).sort(),
    [dashboardRecords],
  )

  const accountCounts = useMemo(() => {
    const counts: Record<string, number> = {}
    for (const r of dashboardRecords) counts[r.account] = (counts[r.account] ?? 0) + 1
    return counts
  }, [dashboardRecords])

  const duplicateGroups = useMemo(() => {
    const groups = new Map<string, DashboardRecord[]>()
    for (const r of dashboardRecords) {
      const token = extractExposeId(r.objectLink) || r.objectId || r.objectLink
      const key = `${r.account}|${token}`
      const list = groups.get(key) ?? []
      list.push(r)
      groups.set(key, list)
    }
    const result: { key: string; keep: DashboardRecord; remove: DashboardRecord[] }[] = []
    for (const [key, list] of Array.from(groups.entries())) {
      if (list.length < 2) continue
      // Most complete record wins: sent mails -> status progress -> details,
      // ties broken by the oldest record (the original one)
      const ranked = [...list].sort((a, b) => {
        const scoreDiff = keepScore(b) - keepScore(a)
        if (scoreDiff !== 0) return scoreDiff
        const timeDiff = createdTimestamp(a) - createdTimestamp(b)
        if (timeDiff !== 0) return timeDiff
        return (a.id ?? 0) - (b.id ?? 0)
      })
      result.push({ key, keep: ranked[0], remove: ranked.slice(1) })
    }
    return result
  }, [dashboardRecords])

  const duplicateIds = useMemo(() => {
    const ids = new Set<number>()
    for (const group of duplicateGroups) {
      for (const r of [group.keep, ...group.remove]) {
        if (r.id !== undefined) ids.add(r.id)
      }
    }
    return ids
  }, [duplicateGroups])

  const duplicateRemoveIds = useMemo(
    () =>
      duplicateGroups
        .flatMap((group) => group.remove)
        .map((r) => r.id)
        .filter((id): id is number => typeof id === "number"),
    [duplicateGroups],
  )

  const duplicateCount = duplicateIds.size
  const duplicateGroupCount = duplicateGroups.length

  const openAdd = () => {
    setEditingId(null)
    setForm({ ...emptyForm })
    setDuplicateError("")
    setUrlError("")
    setOpen(true)
  }

  const openEdit = (record: DashboardRecord) => {
    setEditingId(record.id ?? null)
    setForm(record)
    setDuplicateError("")
    setUrlError("")
    setOpen(true)
  }

  const operatorOptions = useMemo(
    () =>
      Array.from(
        new Set(dashboardRecords.map((r) => r.operator).filter((op): op is string => Boolean(op))),
      ).sort((a, b) => a.localeCompare(b)),
    [dashboardRecords],
  )

  const filtered = useMemo(
    () =>
      dashboardRecords.filter((row) => {
        if (accountFilter && row.account !== accountFilter) return false
        if (operatorFilter && row.operator !== operatorFilter) return false
        if (duplicatesOnly && !(row.id !== undefined && duplicateIds.has(row.id))) return false
        return Object.values(row).some((v) => {
          if (typeof v === "object" && v !== null) {
            return Object.entries(v)
              .filter(([, val]) => val)
              .map(([key]) => key)
              .join(", ")
              .toLowerCase()
              .includes(search.toLowerCase())
          }
          if (typeof v === "string") {
            return v.toLowerCase().includes(search.toLowerCase())
          }
          return false
        })
      }),
    [dashboardRecords, search, accountFilter, operatorFilter, duplicatesOnly, duplicateIds],
  )

  const sorted = useMemo(() => {
    let result = filtered
    if (statusSort !== "none") {
      const statusOrder = Object.fromEntries(STATUSES.map((s, i) => [s, i]))
      result = [...result].sort((a, b) => {
        const orderA = statusOrder[a.status as keyof typeof statusOrder] ?? 99
        const orderB = statusOrder[b.status as keyof typeof statusOrder] ?? 99
        return statusSort === "asc" ? orderA - orderB : orderB - orderA
      })
    }
    if (sourceSort !== "none") {
      result = [...result].sort((a, b) => {
        const aVal = SOURCE_ORDER[a.source] ?? 99
        const bVal = SOURCE_ORDER[b.source] ?? 99
        return sourceSort === "asc" ? aVal - bVal : bVal - aVal
      })
    }
    if (terminSort !== "none") {
      result = [...result].sort((a, b) => {
        const dateA = a.terminDate ? new Date(a.terminDate).getTime() : 0
        const dateB = b.terminDate ? new Date(b.terminDate).getTime() : 0
        if (dateA === 0 && dateB === 0) return 0
        if (dateA === 0) return 1
        if (dateB === 0) return -1
        return terminSort === "asc" ? dateA - dateB : dateB - dateA
      })
    }
    return result
  }, [filtered, statusSort, sourceSort, terminSort])

  useEffect(() => {
    setPage(1)
  }, [search, accountFilter, operatorFilter, duplicatesOnly, statusSort, sourceSort, terminSort])

  const totalPages = Math.max(1, Math.ceil(sorted.length / PAGE_SIZE))
  const currentPage = Math.min(page, totalPages)
  const paged = useMemo(
    () => sorted.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE),
    [sorted, currentPage],
  )

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    setDuplicateError("")
    setUrlError("")

    if (!/^https?:\/\/.+/.test(form.objectLink.trim())) {
      setUrlError(t("error_url"))
      return
    }

    const dto: CreateDashboardDto = {
      accountId: form.account,
      strategy: form.strategy,
      objectId: form.objectId,
      objectLink: form.objectLink,
      status: form.status,
      terminDate: form.terminDate,
      comment: form.comment,
      cooperative: form.cooperative,
      origin: form.origin,
      source: form.source,
      mails: form.mails,
    }

    if (editingId !== null) {
      const oldRecord = dashboardRecords.find((r) => r.id === editingId)
      if (oldRecord) {
        const changes: string[] = []
        const compareKeys: (keyof DashboardRecord)[] = [
          "date",
          "time",
          "client",
          "account",
          "strategy",
          "objectId",
          "objectLink",
          "status",
          "terminDate",
          "comment",
          "cooperative",
          "origin",
          "archivedAt",
        ]
        for (const key of compareKeys) {
          if (oldRecord[key] !== form[key]) {
            changes.push(`${key}: ${oldRecord[key]} → ${form[key]}`)
          }
        }
        const oldMails = Object.entries(oldRecord.mails)
          .filter(([, v]) => v)
          .map(([k]) => MAIL_LABEL[k])
          .join(",")
        const newMails = Object.entries(form.mails)
          .filter(([, v]) => v)
          .map(([k]) => MAIL_LABEL[k])
          .join(",")
        if (oldMails !== newMails) {
          changes.push(`mails: [${oldMails || "—"}] → [${newMails || "—"}]`)
        }
        if (changes.length > 0) {
          logAction(
            "dashboard",
            "edit",
            `${form.client} / ${form.account}: ${changes.join("; ")}`,
            form.client,
            form.account,
          )
        }
      }
      updateMut.mutate({ id: editingId, dto })
      setOpen(false)
      setForm(emptyForm)
      setEditingId(null)
    } else {
      const duplicate = dashboardRecords.find(
        (r) =>
          r.account === form.account &&
          (r.objectLink === form.objectLink || r.objectId === form.objectId) &&
          r.strategy === form.strategy,
      )
      if (duplicate) {
        const field =
          duplicate.objectLink === form.objectLink ? t("form_object_link") : t("form_object_id")
        setDuplicateError(
          `A record with this ${field} already exists (${duplicate.client} / ${duplicate.account})`,
        )
        return
      }
      logAction(
        "dashboard",
        "add",
        `Added dashboard record (${form.client} / ${form.account})`,
        form.client,
        form.account,
      )
      createMut.mutate(dto)
      setOpen(false)
      setForm(emptyForm)
      setEditingId(null)
    }
  }

  const handleCopy = async (url: string, index: number) => {
    await navigator.clipboard.writeText(url)
    setCopiedIndex(index)
    setTimeout(() => setCopiedIndex(null), 1500)
    toast(t("toast_copied"))
  }

  const ARCHIVABLE_STATUSES = ["No relevant", "Absage", "Denied", "Completed"]

  const canArchive = (status: string) => ARCHIVABLE_STATUSES.includes(status)

  const handleArchive = (record: DashboardRecord) => {
    if (!record.id) return
    if (!canArchive(record.status)) {
      toast(t("toast_archive_not_allowed"), "error")
      return
    }
    logAction(
      "dashboard",
      "edit",
      `Archived record (${record.client} / ${record.account})`,
      record.client,
      record.account,
    )
    archiveMut.mutate(record.id)
  }

  const openDeleteDuplicates = () => {
    setDupConfirmText("")
    setDupOpen(true)
  }

  const handleDeleteDuplicates = async () => {
    if (duplicateRemoveIds.length === 0 || removingDup) return
    setRemovingDup(true)
    try {
      const { deleted, failed } = await deleteDupMut.mutateAsync(duplicateRemoveIds)
      if (deleted > 0) {
        logAction(
          "dashboard",
          "edit",
          `Removed ${deleted} duplicate record(s) in ${duplicateGroupCount} group(s)`,
        )
        toast(t("toast_duplicates_removed", String(deleted)))
      }
      if (failed > 0) toast(t("toast_duplicates_failed", String(failed)), "error")
      setDupOpen(false)
      setDupConfirmText("")
    } catch {
      // error toast is shown by the mutation itself
    } finally {
      setRemovingDup(false)
    }
  }

  if (isLoading) {
    return (
      <div className="p-6 flex items-center justify-center h-64">
        <div className="text-muted-foreground">Loading...</div>
      </div>
    )
  }

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={t("search_records")}
              className="h-10 w-full max-w-sm rounded-md border border-input bg-background pl-10 pr-3 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            />
          </div>
          <select
            value={accountFilter}
            onChange={(e) => setAccountFilter(e.target.value)}
            className="h-10 rounded-md border border-input bg-background px-3 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            <option value="">{t("filter_all_accounts")}</option>
            {uniqueAccounts.map((a) => (
              <option key={a} value={a}>
                {a} ({accountClientMap[a] || "—"}) ({accountCounts[a] ?? 0})
              </option>
            ))}
          </select>
          {isAdmin && operatorOptions.length > 0 && (
            <select
              value={operatorFilter}
              onChange={(e) => setOperatorFilter(e.target.value)}
              className="h-10 rounded-md border border-input bg-background px-3 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              <option value="">{t("filter_all_operators")}</option>
              {operatorOptions.map((op) => (
                <option key={op} value={op}>
                  {op}
                </option>
              ))}
            </select>
          )}
          <button
            type="button"
            onClick={() => setDuplicatesOnly((v) => !v)}
            disabled={duplicateCount === 0}
            title={duplicateCount > 0 ? t("duplicates_count", String(duplicateCount)) : undefined}
            className={`inline-flex items-center gap-1.5 h-10 rounded-md border px-3 text-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${
              duplicatesOnly
                ? "border-amber-500 bg-amber-50 text-amber-700 dark:bg-amber-950 dark:text-amber-300"
                : "border-input bg-background hover:bg-accent"
            } ${duplicateCount === 0 ? "opacity-50 cursor-not-allowed" : "cursor-pointer"}`}
          >
            <Archive className="h-4 w-4" />
            {t("filter_duplicates_only")}
            {duplicateCount > 0 && (
              <span className="inline-flex items-center rounded-full px-1.5 py-0.5 text-[11px] font-semibold bg-amber-500 text-white">
                {duplicateCount}
              </span>
            )}
          </button>
          <button
            type="button"
            onClick={openDeleteDuplicates}
            disabled={duplicateRemoveIds.length === 0}
            title={
              duplicateRemoveIds.length > 0
                ? t("duplicates_delete_count", String(duplicateRemoveIds.length))
                : t("duplicates_none")
            }
            className={`inline-flex items-center gap-1.5 h-10 rounded-md border px-3 text-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${
              duplicateRemoveIds.length > 0
                ? "border-red-300 bg-background text-red-600 hover:bg-red-50 dark:border-red-900 dark:text-red-400 dark:hover:bg-red-950 cursor-pointer"
                : "border-input bg-background opacity-50 cursor-not-allowed"
            }`}
          >
            <CopyX className="h-4 w-4" />
            {t("btn_delete_duplicates")}
            {duplicateRemoveIds.length > 0 && (
              <span className="inline-flex items-center rounded-full px-1.5 py-0.5 text-[11px] font-semibold bg-red-500 text-white">
                {duplicateRemoveIds.length}
              </span>
            )}
          </button>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={() => setHideSensitive((v) => !v)}
            className="inline-flex items-center gap-1.5 h-9 px-4 rounded-md border border-input bg-background text-sm font-medium hover:bg-accent transition-colors"
            title={hideSensitive ? t("btn_show_sensitive") : t("btn_hide_sensitive")}
          >
            {hideSensitive ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            {hideSensitive ? t("btn_show_sensitive") : t("btn_hide_sensitive")}
          </button>
          <Dialog
            open={open}
            onOpenChange={(v) => {
              if (!v) {
                setOpen(false)
                setEditingId(null)
                setDuplicateError("")
                setUrlError("")
              } else {
                setOpen(true)
              }
            }}
          >
            <DialogTrigger asChild>
              <button
                onClick={openAdd}
                className="inline-flex items-center gap-1.5 h-9 px-4 rounded-md bg-primary text-primary-foreground text-sm font-medium hover:bg-primary/90 transition-colors"
              >
                <Plus className="h-4 w-4" />
                {t("btn_add_record")}
              </button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-3xl">
              <DialogHeader>
                <DialogTitle>
                  {editingId !== null ? t("dialog_edit_dashboard") : t("dialog_add_dashboard")}
                </DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid gap-8 sm:grid-cols-[1fr_320px]">
                  <div className="space-y-3">
                    {(["objectId", "objectLink"] as const).map((field) => (
                      <div key={field} className="space-y-1.5">
                        <label className="text-sm font-medium capitalize">
                          {t(field === "objectId" ? "form_object_id" : "form_object_link")}
                        </label>
                        <input
                          value={form[field]}
                          onChange={(e) => setForm({ ...form, [field]: e.target.value })}
                          placeholder={field}
                          className="h-9 w-full rounded-md border border-input bg-background px-3 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                          required
                        />
                      </div>
                    ))}
                    <div className="space-y-1.5">
                      <label className="text-sm font-medium capitalize">{t("form_account")}</label>
                      <select
                        value={form.account}
                        onChange={(e) => {
                          const accId = e.target.value
                          const acc = accounts.find((a) => a.accountId === accId)
                          setForm({
                            ...form,
                            account: accId,
                            client: acc?.clientName ?? form.client,
                            strategy: acc?.strategy ?? form.strategy,
                          })
                        }}
                        className="h-9 w-full rounded-md border border-input bg-background px-3 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                        required
                      >
                        <option value="" disabled>
                          {t("placeholder_select_account")}
                        </option>
                        {accountList.map((a) => (
                          <option key={a} value={a}>
                            {a} ({accountClientMap[a] || "—"})
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="space-y-1.5">
                      <label className="text-sm font-medium">{t("form_comment")}</label>
                      <textarea
                        value={form.comment}
                        onChange={(e) => setForm({ ...form, comment: e.target.value })}
                        placeholder={t("placeholder_add_comment")}
                        rows={3}
                        maxLength={1000}
                        className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring resize-none"
                      />
                      <p className="text-xs text-muted-foreground text-right">
                        {form.comment.length}/1000
                      </p>
                    </div>
                    <div className="space-y-1.5">
                      <label className="text-sm font-medium capitalize">{t("form_status")}</label>
                      <select
                        value={form.status}
                        onChange={(e) => setForm({ ...form, status: e.target.value })}
                        className="h-9 w-full rounded-md border border-input bg-background px-3 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                        required
                      >
                        <option value="" disabled>
                          {t("placeholder_select_status")}
                        </option>
                        {STATUSES.map((s) => (
                          <option key={s} value={s}>
                            {t(STATUS_T_KEY[s])}
                          </option>
                        ))}
                      </select>
                    </div>
                    {form.status === "Termin invitation" && (
                      <div className="space-y-1.5">
                        <label className="text-sm font-medium capitalize">
                          {t("form_termin_date")}
                        </label>
                        <input
                          type="datetime-local"
                          value={form.terminDate}
                          onChange={(e) => setForm({ ...form, terminDate: e.target.value })}
                          className="h-9 w-full rounded-md border border-input bg-background px-3 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                        />
                      </div>
                    )}
                    <div className="space-y-1.5">
                      <label className="text-sm font-medium">{t("form_cooperative")}</label>
                      <input
                        value={form.cooperative}
                        onChange={(e) => setForm({ ...form, cooperative: e.target.value })}
                        placeholder={t("form_cooperative")}
                        className="h-9 w-full rounded-md border border-input bg-background px-3 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                      />
                    </div>
                    <div className="space-y-1.5">
                      <label className="text-sm font-medium capitalize">{t("th_origin")}</label>
                      <select
                        value={form.origin}
                        onChange={(e) => setForm({ ...form, origin: e.target.value })}
                        className="h-9 w-full rounded-md border border-input bg-background px-3 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                      >
                        <option value="">—</option>
                        {ORIGINS.map((o) => (
                          <option key={o} value={o}>
                            {o}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                  <div className="space-y-4 sm:pt-1.5">
                    <div className="space-y-1.5">
                      <label className="text-sm font-medium capitalize">{t("form_source")}</label>
                      <select
                        value={form.source}
                        onChange={(e) =>
                          setForm({ ...form, source: e.target.value as DashboardRecord["source"] })
                        }
                        className="h-9 w-full rounded-md border border-input bg-background px-3 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                      >
                        {SOURCES.map((s) => (
                          <option key={s} value={s}>
                            {t(
                              s === "Manual"
                                ? "label_manual"
                                : s === "Immomio"
                                  ? "label_immomio"
                                  : "label_auto",
                            )}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="space-y-2">
                      <label className="text-sm font-medium">{t("form_sent_mails")}</label>
                      <div className="flex flex-col gap-2">
                        {(["M2", "M3", "M4", "M5", "M6"] as const).map((m) => {
                          const fullName: Record<string, string> = {
                            M2: "Gratitude",
                            M3: "Motivation",
                            M4: "Feedback",
                            M5: "After Review",
                            M6: "",
                          }
                          return (
                            <label
                              key={m}
                              className="flex items-center gap-1.5 text-sm cursor-pointer justify-between"
                            >
                              {MAIL_LABEL[m]}
                              {fullName[m] && (
                                <span className="text-muted-foreground"> ({fullName[m]})</span>
                              )}
                              <Switch
                                checked={form.mails[m]}
                                onCheckedChange={(checked) =>
                                  setForm({
                                    ...form,
                                    mails: { ...form.mails, [m]: checked },
                                  })
                                }
                              />
                            </label>
                          )
                        })}
                      </div>
                    </div>
                  </div>
                </div>
                {(duplicateError || urlError) && (
                  <div className="text-sm text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-950 rounded-md px-3 py-2">
                    {duplicateError || urlError}
                  </div>
                )}
                <div className="flex justify-end gap-3 pt-2">
                  <DialogClose asChild>
                    <button
                      type="button"
                      className="h-9 px-4 rounded-md border border-input bg-background text-sm hover:bg-accent transition-colors"
                    >
                      {t("btn_cancel")}
                    </button>
                  </DialogClose>
                  <button
                    type="submit"
                    className="h-9 px-4 rounded-md bg-primary text-primary-foreground text-sm font-medium hover:bg-primary/90 transition-colors"
                  >
                    {t("btn_save")}
                  </button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
          <Dialog
            open={deletingId !== null}
            onOpenChange={(v) => {
              if (!v) setDeletingId(null)
            }}
          >
            <DialogContent className="sm:max-w-md">
              <DialogHeader>
                <DialogTitle>{t("dialog_delete_record")}</DialogTitle>
              </DialogHeader>
              <p className="text-sm text-muted-foreground">{t("dialog_delete_confirm")}</p>
              <div className="flex justify-end gap-3 pt-2">
                <DialogClose asChild>
                  <button
                    type="button"
                    className="h-9 px-4 rounded-md border border-input bg-background text-sm hover:bg-accent transition-colors"
                  >
                    {t("btn_no")}
                  </button>
                </DialogClose>
                <button
                  onClick={() => {
                    if (deletingId !== null) deleteMut.mutate(deletingId)
                    setDeletingId(null)
                  }}
                  className="h-9 px-4 rounded-md bg-destructive text-destructive-foreground text-sm font-medium hover:bg-destructive/90 transition-colors"
                >
                  {t("btn_yes")}
                </button>
              </div>
            </DialogContent>
          </Dialog>
          <Dialog
            open={dupOpen}
            onOpenChange={(v) => {
              if (!v) setDupConfirmText("")
              setDupOpen(v)
            }}
          >
            <DialogContent className="sm:max-w-2xl">
              <DialogHeader>
                <DialogTitle>{t("dialog_delete_duplicates")}</DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <p className="text-sm">{t("duplicates_groups_count", String(duplicateGroupCount))}</p>
                <p className="text-sm text-muted-foreground">
                  {t("duplicates_delete_count", String(duplicateRemoveIds.length))}
                </p>
                <p className="text-xs text-muted-foreground">{t("duplicates_keep_rule")}</p>
                <div className="rounded-md border overflow-x-auto max-h-64">
                  <table className="text-xs w-full whitespace-nowrap">
                    <thead>
                      <tr className="border-b bg-muted/50 text-left">
                        <th className="px-2 py-1.5 font-medium">{t("th_account")}</th>
                        <th className="px-2 py-1.5 font-medium">{t("th_client")}</th>
                        <th className="px-2 py-1.5 font-medium">{t("th_object_id")}</th>
                        <th className="px-2 py-1.5 font-medium">{t("th_status")}</th>
                        <th className="px-2 py-1.5 font-medium">{t("th_group_size")}</th>
                        <th className="px-2 py-1.5 font-medium">{t("th_will_delete")}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {duplicateGroups.map((group) => (
                        <tr key={group.key} className="border-b last:border-0">
                          <td className="px-2 py-1.5 font-mono">{group.keep.account || "—"}</td>
                          <td className="px-2 py-1.5">{group.keep.client || "—"}</td>
                          <td className="px-2 py-1.5 font-mono">
                            {extractExposeId(group.keep.objectLink) || group.keep.objectId || "—"}
                          </td>
                          <td className="px-2 py-1.5">{t(STATUS_T_KEY[group.keep.status])}</td>
                          <td className="px-2 py-1.5 font-semibold">
                            ×{group.remove.length + 1}
                          </td>
                          <td className="px-2 py-1.5 font-semibold text-red-600 dark:text-red-400">
                            −{group.remove.length}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <p className="text-sm text-muted-foreground">{t("duplicates_type_confirm")}</p>
                <input
                  value={dupConfirmText}
                  onChange={(e) => setDupConfirmText(e.target.value)}
                  placeholder="delete duplicates"
                  className="h-9 w-full rounded-md border border-input bg-background px-3 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                />
                <div className="flex justify-end gap-2">
                  <DialogClose asChild>
                    <button
                      type="button"
                      className="h-9 px-4 rounded-md border border-input bg-background text-sm font-medium hover:bg-accent transition-colors"
                    >
                      {t("btn_cancel")}
                    </button>
                  </DialogClose>
                  <button
                    onClick={handleDeleteDuplicates}
                    disabled={dupConfirmText !== "delete duplicates" || removingDup}
                    className="inline-flex items-center gap-1.5 h-9 px-4 rounded-md bg-red-600 text-white text-sm font-medium hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <Trash2 className="h-4 w-4" />
                    {removingDup ? t("duplicates_removing") : t("btn_delete")}
                  </button>
                </div>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <div className="rounded-lg border bg-card">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{t("th_time")}</TableHead>
              <TableHead>{t("th_alive")}</TableHead>
              <TableHead>{t("th_client")}</TableHead>
              <TableHead>{t("th_account")}</TableHead>
              {isAdmin && <TableHead>{t("th_operator")}</TableHead>}
              <TableHead>
                <button
                  type="button"
                  onClick={() =>
                    setSourceSort(
                      sourceSort === "none" ? "asc" : sourceSort === "asc" ? "desc" : "none",
                    )
                  }
                  className="inline-flex items-center gap-1 hover:text-foreground transition-colors"
                >
                  {t("th_source")}
                  {sourceSort === "asc" ? (
                    <ArrowUp className="h-3.5 w-3.5" />
                  ) : sourceSort === "desc" ? (
                    <ArrowDown className="h-3.5 w-3.5" />
                  ) : (
                    <ArrowUpDown className="h-3.5 w-3.5 opacity-40" />
                  )}
                </button>
              </TableHead>
              <TableHead>{t("th_origin")}</TableHead>
              <TableHead>{t("th_object_id")}</TableHead>
              <TableHead>{t("th_object_link")}</TableHead>
              <TableHead>
                <button
                  type="button"
                  onClick={() =>
                    setStatusSort(
                      statusSort === "none" ? "asc" : statusSort === "asc" ? "desc" : "none",
                    )
                  }
                  className="inline-flex items-center gap-1 hover:text-foreground transition-colors"
                >
                  {t("th_status")}
                  {statusSort === "asc" ? (
                    <ArrowUp className="h-3.5 w-3.5" />
                  ) : statusSort === "desc" ? (
                    <ArrowDown className="h-3.5 w-3.5" />
                  ) : (
                    <ArrowUpDown className="h-3.5 w-3.5 opacity-40" />
                  )}
                </button>
              </TableHead>
              <TableHead>
                <button
                  type="button"
                  onClick={() =>
                    setTerminSort(
                      terminSort === "none" ? "asc" : terminSort === "asc" ? "desc" : "none",
                    )
                  }
                  className="inline-flex items-center gap-1 hover:text-foreground transition-colors"
                >
                  {t("th_termin_date")}
                  {terminSort === "asc" ? (
                    <ArrowUp className="h-3.5 w-3.5" />
                  ) : terminSort === "desc" ? (
                    <ArrowDown className="h-3.5 w-3.5" />
                  ) : (
                    <ArrowUpDown className="h-3.5 w-3.5 opacity-40" />
                  )}
                </button>
              </TableHead>
              <TableHead>{t("th_mails")}</TableHead>
              <TableHead>{t("th_cooperative")}</TableHead>
              <TableHead>{t("th_comment")}</TableHead>
              <TableHead className="w-24">{t("th_actions")}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {sorted.length === 0 ? (
              <TableRow>
                <TableCell colSpan={14 + (isAdmin ? 1 : 0)} className="text-center text-muted-foreground py-8">
                  {t("empty_no_results")}
                </TableCell>
              </TableRow>
            ) : (
              paged.map((row, i) => {
                const isDuplicate = row.id !== undefined && duplicateIds.has(row.id)
                return (
                  <TableRow
                    key={row.id ?? i}
                    className={
                      isDuplicate
                        ? "bg-amber-50 dark:bg-amber-950/40 hover:bg-amber-100 dark:hover:bg-amber-950/60"
                        : undefined
                    }
                  >
                    <TableCell>{row.time}</TableCell>
                    <TableCell className="text-xs font-mono whitespace-nowrap">
                      {calcAlive(row.date, row.time)}
                    </TableCell>
                    <TableCell>
                      <span className="flex items-center gap-2 whitespace-nowrap">
                        <span
                          className="inline-block w-2.5 h-2.5 rounded-full shrink-0"
                          style={{
                            backgroundColor: clientColorMap[row.client] || getUserColor(row.client),
                          }}
                        />
                        <span className={hideSensitive ? "blur-sm select-none" : ""}>
                          {row.client}
                        </span>
                      </span>
                    </TableCell>
                    <TableCell className="font-mono text-xs">
                      <div className="flex items-center gap-1.5">
                        <span className={hideSensitive ? "blur-sm select-none" : ""}>
                          {row.account}
                        </span>
                        {isDuplicate && (
                          <span className="inline-flex items-center rounded-md bg-amber-100 px-1.5 py-0.5 text-[11px] font-semibold text-amber-700 dark:bg-amber-900 dark:text-amber-300">
                            {t("label_duplicate")}
                          </span>
                        )}
                      </div>
                    </TableCell>
                    {isAdmin && (
                      <TableCell>
                        {row.operator || (
                          <span className="text-muted-foreground">—</span>
                        )}
                      </TableCell>
                    )}
                    <TableCell>
                      <span
                        className={`inline-flex items-center rounded-md px-2 py-0.5 text-xs font-medium ${
                          row.source === "Auto"
                            ? "bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300"
                            : row.source === "Immomio"
                              ? "bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300"
                              : "bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400"
                        }`}
                      >
                        {t(
                          row.source === "Auto"
                            ? "label_auto"
                            : row.source === "Immomio"
                              ? "label_immomio"
                              : "label_manual",
                        )}
                      </span>
                    </TableCell>
                    <TableCell>
                      <span className="text-xs">{row.origin || "—"}</span>
                    </TableCell>
                    <TableCell className="font-mono text-xs whitespace-nowrap">
                      {row.objectId}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1.5">
                        <span className="font-mono text-xs">
                          {extractExposeId(row.objectLink) || row.objectLink}
                        </span>
                        <button
                          onClick={() => handleCopy(row.objectLink, i)}
                          className="h-6 w-6 rounded hover:bg-accent flex items-center justify-center transition-colors"
                          title={row.objectLink}
                        >
                          {copiedIndex === i ? (
                            <Check className="h-3 w-3 text-green-500" />
                          ) : (
                            <Copy className="h-3 w-3" />
                          )}
                        </button>
                        <a
                          href={row.objectLink}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="h-6 w-6 rounded hover:bg-accent flex items-center justify-center transition-colors"
                          title={t("tip_open_link")}
                        >
                          <ExternalLink className="h-3 w-3" />
                        </a>
                      </div>
                    </TableCell>
                    <TableCell className="whitespace-nowrap">
                      <Badge variant={statusVariant(row.status)} className="uppercase font-bold">
                        {t(STATUS_T_KEY[row.status])}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-xs font-bold">
                      {row.terminDate ? (
                        <span>
                          {new Date(row.terminDate).toLocaleDateString("de-DE", {
                            day: "2-digit",
                            month: "2-digit",
                          })}{" "}
                          {new Date(row.terminDate).toLocaleTimeString("de-DE", {
                            hour: "2-digit",
                            minute: "2-digit",
                          })}
                        </span>
                      ) : (
                        <span className="text-muted-foreground">—</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        {(["M2", "M3", "M4", "M5", "M6"] as const).map((m) => (
                          <span
                            key={m}
                            className={`inline-flex items-center rounded-md px-1.5 py-0.5 text-[11px] font-medium ${
                              row.mails[m]
                                ? "bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300"
                                : "bg-gray-100 text-gray-400 dark:bg-gray-800 dark:text-gray-500"
                            }`}
                          >
                            {MAIL_LABEL[m]}
                          </span>
                        ))}
                      </div>
                    </TableCell>
                    <TableCell>
                      <span className="text-xs text-muted-foreground">
                        {row.cooperative || "—"}
                      </span>
                    </TableCell>
                    <TableCell>
                      <span
                        className={`text-xs text-muted-foreground max-w-[160px] inline-block truncate ${
                          hideSensitive ? "blur-sm select-none" : ""
                        }`}
                        title={hideSensitive ? undefined : row.comment || ""}
                      >
                        {row.comment || "—"}
                      </span>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        <button
                          onClick={() => openEdit(row)}
                          className="h-8 w-8 rounded-md border border-input bg-background flex items-center justify-center hover:bg-accent transition-colors"
                          title={t("tip_edit")}
                        >
                          <Pencil className="h-3.5 w-3.5" />
                        </button>
                        <button
                          onClick={() => handleArchive(row)}
                          disabled={!canArchive(row.status)}
                          className={`h-8 w-8 rounded-md border border-input bg-background flex items-center justify-center transition-colors ${
                            canArchive(row.status)
                              ? "hover:bg-accent"
                              : "opacity-40 cursor-not-allowed"
                          }`}
                          title={
                            canArchive(row.status) ? t("tip_archive") : t("tip_archive_disabled")
                          }
                        >
                          <Archive className="h-3.5 w-3.5" />
                        </button>
                        {row.id !== undefined && (
                          <button
                            onClick={() => setDeletingId(row.id!)}
                            className="h-8 w-8 rounded-md border border-input bg-background flex items-center justify-center hover:bg-accent transition-colors text-destructive"
                            title={t("btn_delete")}
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                )
              })
            )}
          </TableBody>
        </Table>
        {sorted.length > 0 && (
          <div className="flex items-center justify-between border-t px-4 py-3 text-sm">
            <span className="text-muted-foreground">
              {t(
                "pagination_summary",
                String((currentPage - 1) * PAGE_SIZE + 1),
                String(Math.min(currentPage * PAGE_SIZE, sorted.length)),
                String(sorted.length),
              )}
            </span>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => setPage(currentPage - 1)}
                disabled={currentPage <= 1}
                className="inline-flex h-8 items-center gap-1 rounded-md border border-input bg-background px-3 text-sm font-medium transition-colors hover:bg-accent disabled:cursor-not-allowed disabled:opacity-40"
              >
                <ChevronLeft className="h-4 w-4" />
                {t("pagination_prev")}
              </button>
              <span className="text-muted-foreground">
                {t("pagination_page", String(currentPage), String(totalPages))}
              </span>
              <button
                type="button"
                onClick={() => setPage(currentPage + 1)}
                disabled={currentPage >= totalPages}
                className="inline-flex h-8 items-center gap-1 rounded-md border border-input bg-background px-3 text-sm font-medium transition-colors hover:bg-accent disabled:cursor-not-allowed disabled:opacity-40"
              >
                {t("pagination_next")}
                <ChevronRight className="h-4 w-4" />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default Dashboard
